import { useState } from "react";
import { ExternalLink, Handshake, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { del, errorMessage, get } from "@/lib/api";
import { useAsync } from "@/lib/queries";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { PartnerOfferDialog } from "@/components/PartnerOfferDialog";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorState, LoadingRows, PageHeader } from "@/components/States";

const OfferCard = ({ offer, canEdit, onEdit, onDelete }) => (
  <article className="flex min-h-full flex-col border-t-2 border-emerald-600 bg-white p-5" data-testid={`partner-offer-${offer.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{offer.partner_name}</p>
        <h2 className="font-display mt-1 text-xl font-bold text-slate-950" data-testid={`partner-offer-title-${offer.id}`}>{offer.offer_title}</h2>
      </div>
      {!offer.active && <span className="shrink-0 rounded border border-slate-300 px-2 py-1 text-xs font-semibold text-slate-600">Draft</span>}
    </div>
    <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700">{offer.description}</p>
    {offer.code && <div className="mt-4 flex items-center justify-between gap-3 border-y border-dashed border-slate-300 py-3">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Discount code</span>
      <code className="break-all font-mono text-sm font-bold text-slate-900" data-testid={`partner-offer-code-${offer.id}`}>{offer.code}</code>
    </div>}
    {offer.terms && <p className="mt-3 whitespace-pre-wrap text-xs leading-5 text-slate-500">{offer.terms}</p>}
    <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-4">
      {offer.url ? <a href={offer.url} target="_blank" rel="noreferrer" className="inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-blue-700 hover:underline" data-testid={`partner-offer-link-${offer.id}`}>
        Visit partner <ExternalLink className="h-4 w-4" aria-hidden="true" />
      </a> : <span />}
      {canEdit && <div className="flex gap-1">
        <Button type="button" size="icon" variant="ghost" onClick={() => onEdit(offer)} aria-label="Edit offer" title="Edit offer" data-testid={`partner-offer-edit-${offer.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button type="button" size="icon" variant="ghost" onClick={() => onDelete(offer)} aria-label="Delete offer" title="Delete offer" data-testid={`partner-offer-delete-${offer.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
      </div>}
    </div>
  </article>
);

export default function Partners() {
  const { profile } = useAuth();
  const canEdit = !!profile?.is_platform_owner;
  const { data, loading, error, reload } = useAsync(() => get(canEdit ? "/platform/partners" : "/partners"), [canEdit]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const offers = data || [];

  const remove = async () => {
    if (!deleting) return;
    try {
      await del(`/platform/partners/${deleting.id}`);
      toast.success("Partner offer removed");
      setDeleting(null);
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="partners-page">
      <PageHeader eyebrow="Member benefits" title="Partners" description="Discounts and offers from our partners."
        actions={canEdit && <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-blue-700 hover:bg-blue-800" data-testid="partner-add-button"><Plus className="mr-2 h-4 w-4" />Add offer</Button>} />
      {error && <ErrorState message={error} onRetry={reload} />}
      {loading && !data && <LoadingRows rows={3} />}
      {!loading && !error && !offers.length && <EmptyState icon={Handshake} title="No partner offers yet" description={canEdit ? "Add an offer to make it available to logged-in users." : "New offers will appear here when available."} testId="partners-empty" />}
      {!!offers.length && <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="partner-offer-list">
        {offers.map((offer) => <OfferCard key={offer.id} offer={offer} canEdit={canEdit} onEdit={(item) => { setEditing(item); setDialogOpen(true); }} onDelete={setDeleting} />)}
      </section>}
      {canEdit && <PartnerOfferDialog open={dialogOpen} onOpenChange={setDialogOpen} offer={editing} onSaved={reload} />}
      <ConfirmDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)} onConfirm={remove} title={`Delete ${deleting?.partner_name || "this"} offer?`}
        description="This offer will no longer be available to logged-in users." confirmLabel="Delete offer" destructive />
    </div>
  );
}